import {
    Brokers,
    ConnectTradingAccountResponse,
    GetBrokerLoginUrlRequest,
    GetBrokerLoginUrlResponse,
    TradingAccount,
    TradingAccountMargin,
    TradingAccounts
} from '../../proto/generated/trading-bridge'
import commonActions from '../../actions/common.actions'
import api from './axiosClient'
import store, { AppDispatch } from '../../store'
import { setMarginForAccount, setTradingAccounts, UserStateType } from '../../reducer/user.reducer'
import { PRIMARY_TRADING_ACCOUNT_KEY } from '../../assets/constants'
import { MarginFromBrokerRequest, MarginFromBrokerResponse } from '../../proto/generated/order'
import { PositionRequest } from '../../proto/generated/position'
import { getOrders, getPositions } from './orders'
import { updatePositions } from '../../reducer/positions.reducer'
import { setOrderHistory } from '../../reducer/orders.reducer'


export const getBrokers = async (): Promise<Brokers> => {
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/brokers`
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = Brokers.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const getTradingAccounts = async (): Promise<TradingAccounts> => {
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts`
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccounts.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const removeTradingAccount = async (args: { body: any }): Promise<TradingAccounts> => {
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts`
        api.delete(url, {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            data: JSON.stringify(args.body),
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccounts.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const getBrokerLoginUrl = async (args: { body: GetBrokerLoginUrlRequest }): Promise<GetBrokerLoginUrlResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/login-url`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = GetBrokerLoginUrlResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const getAccountMargins = async (args: { body: any }): Promise<TradingAccountMargin> => {
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/margins`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccountMargin.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const syncAccountMarginFromBroker = async (args: { body: any }): Promise<TradingAccountMargin> => {
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/sync-margin`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccountMargin.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const getRequiredMarginFromBroker = async (args: {request: MarginFromBrokerRequest}): Promise<MarginFromBrokerResponse> => {
    return new Promise((resolve, reject) => {
        const url = `trading-accounts/calculate-margin`
        api.post(url, JSON.stringify(args.request), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async(response)=>{
            const decodedData = MarginFromBrokerResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error)=> {
            reject(error)
        })
    })
}

export const connectTradingAccount = async (args: {
    body: any,
    dispatch: AppDispatch
}): Promise<ConnectTradingAccountResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/connect`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = ConnectTradingAccountResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            commonActions.setApiError(args.dispatch, {
                error: true,
                message: 'Failed connecting trading account',
                errorType: 'error'
            })
            reject(error)
        })
    })
}

export const setAppPrimaryTradingAccount = async (tradingAccountId: string) => {
    let tradingAccounts = (store.getState().userStore as UserStateType)?.tradingAccounts ?? []
    const primaryIndex = tradingAccounts.findIndex((acc) => acc.accountId === tradingAccountId)
    if (primaryIndex > -1) {
        tradingAccounts = tradingAccounts.map((acc, index) => {
            if (index === primaryIndex) {
                return { ...acc, primary: true }
            } else {
                return { ...acc, primary: false }
            }
        })
    } else {
        tradingAccounts = (await getTradingAccounts()).tradingAccounts
        tradingAccounts = tradingAccounts.map((acc, index) => {
            if (index === primaryIndex) {
                return { ...acc, primary: true }
            } else {
                return { ...acc, primary: false }
            }
        })
    }
    refetchPositionsAndOrders(tradingAccounts)
}

export const refetchPositionsAndOrders = async (alreadyTradingAccounts?: TradingAccount[]) => {
    let tradingAccounts = alreadyTradingAccounts ? alreadyTradingAccounts : (await getTradingAccounts())?.tradingAccounts
    let primaryTradingAccount = tradingAccounts?.find((acc) => acc.primary)
    if (!primaryTradingAccount) {
        try {
            const response = await getTradingAccounts()
            tradingAccounts = response.tradingAccounts
            primaryTradingAccount = tradingAccounts.find((acc) => acc.primary)
            store.dispatch(setTradingAccounts(tradingAccounts))
        } catch (error) {
            console.error('Failed to fetch trading accounts', error)
            return
        }
    }
    if (!primaryTradingAccount) {
        return
    }
    const positionRequest = { tradingAccountId: primaryTradingAccount.accountId } as PositionRequest
    getPositions({ body: { positionRequest } })
        .then((response) => {
            store.dispatch(updatePositions({ positions: response.position, overwrite: true }))
        })
        .catch((error) => {
            console.error('Error fetching positions', error)
        })
    getOrders({ body: { tradingAccountId: primaryTradingAccount.accountId } })
        .then((response) => {
            const orders = response.reverse()
            store.dispatch(setOrderHistory({ updates: orders }))
        })
        .catch((error) => {
            console.error('Error fetching orders', error)
        })
}

export const setPrimaryTradingAccountApi = async (args: {
    body: any
}): Promise<TradingAccounts> => {
    return new Promise((resolve, reject) => {
        const url = `/trading-accounts/make-primary`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TradingAccounts.decode(new Uint8Array(response.data))
            store.dispatch(setTradingAccounts(decodedData.tradingAccounts))
            const primaryTradingAccount = decodedData.tradingAccounts.find(acc => acc.primary)
            if (primaryTradingAccount) {
                sessionStorage.setItem(PRIMARY_TRADING_ACCOUNT_KEY, primaryTradingAccount.accountId)
                const body = {
                    accountId: primaryTradingAccount.accountId,
                    brokerCode: primaryTradingAccount.brokerCode,
                    brokerAccountId: primaryTradingAccount.brokerAccountId,
                    created: primaryTradingAccount.created
                }
                // const positions = await getPositions({
                //     body: { positionRequest :  { tradingAccount: primaryTradingAccount }}
                // })
                // store.dispatch(setSymbolWisePositions(positions))
                // const orders = await getOrders({ body: { tradingAccountId: primaryTradingAccount.accountId } })
                // store.dispatch(setOrderHistory({ updates: orders }))
                const margin = await getAccountMargins({
                    body: body
                })
                store.dispatch(setMarginForAccount({ accountId: primaryTradingAccount.accountId, margin: margin }))
            }
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}